import {Component, ContentChild, ElementRef, EventEmitter, Input, OnDestroy, Output, forwardRef} from '@angular/core';
import {ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, Validators} from '@angular/forms';
import {Subject, filter, takeUntil} from 'rxjs';
import {DateTimeRangeLabel, DateTimeRangeSelectComponent} from '../date-time-range-select/date-time-range-select.component';
import {SiteId, SiteInfo} from '@app/shared/services/api/access-sites.interface';
import {Client} from '@app/layout/admin/services/api/client-interfaces';
import {DateTimeRangeMode, RelativeDateRange, RelativeDateTimeRange} from '@shared/components/date-time-range-picker/date-time-range-picker.component';
import {SiteAndRelativeDateRange} from '@shared/components/page-site-and-date-range-select/page-site-and-date-range-select.service';

interface SiteAndDateRangeFormValue {
  siteSelection?: SiteId | SiteId[];
  dateRange?: RelativeDateTimeRange;
}

type SiteDateRangeForm<T = SiteAndDateRangeFormValue> = {
  [Property in keyof T]: FormControl<T[Property]>;
}

@Component({
  selector: 'app-page-site-and-date-picker',
  templateUrl: './page-site-and-date-picker.component.html',
  styleUrls: ['./page-site-and-date-picker.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PageSiteAndDatePickerComponent), multi: true}
  ],
  standalone: false
})
export class PageSiteAndDatePickerComponent implements OnDestroy, ControlValueAccessor {
  private readonly ngUnsubscribe: Subject<void> = new Subject();

  @Input() dateRangeLabels: DateTimeRangeLabel = DateTimeRangeSelectComponent.DEFAULT_LABEL;
  @Input() pickerMode = DateTimeRangeMode.RELATIVE;
  @Input() showIncludeToday: boolean = false;
  @Input() relativeDateRanges: RelativeDateRange[];
  @Input() multiSelect = true;
  @Output() sitesLoaded: EventEmitter<SiteInfo[]> = new EventEmitter();
  @Output() clientsLoaded: EventEmitter<Client[]> = new EventEmitter();
  @ContentChild('content') middleContent: ElementRef;
  isDisabled = false;
  formGroup: FormGroup<SiteDateRangeForm>;
  sitesLoading = true;
  isSingleSiteSelectMode = false;
  onChangeFn = (_sitesAndDateRange: SiteAndRelativeDateRange): void => {};
  onTouchedFn = (): void => {};

  constructor() {
    this.formGroup = this.initForm();
  }

  writeValue(siteAndRelativeDateRange: SiteAndRelativeDateRange): void {
    const dateRange: SiteAndRelativeDateRange | null = siteAndRelativeDateRange ? {...siteAndRelativeDateRange} : null;
    delete dateRange?.siteIds;
    const siteIds = siteAndRelativeDateRange?.siteIds ?? null;
    this.formGroup.setValue({
      siteSelection: this.multiSelect ? siteIds : (siteIds?.[0] ?? null),
      dateRange
    }, {emitEvent: false});
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.formGroup.disable();
    } else {
      if (!this.sitesLoading) {
        this.formGroup.enable();
      } else {
        this.formGroup.controls.siteSelection.enable();
      }
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onSitesLoaded(sites: SiteInfo[]): void {
    this.sitesLoading = false;
    this.isSingleSiteSelectMode = sites.length < 2;
    if (!this.isDisabled) {
      this.formGroup.controls.dateRange.enable();
    }
    this.sitesLoaded.emit(sites);
  }

  onClientsLoaded(clients: Client[]): void {
    this.clientsLoaded.emit(clients);
  }

  private initForm(): FormGroup<SiteDateRangeForm> {
    const formGroup = new FormGroup<SiteDateRangeForm>({
      siteSelection: new FormControl(null, Validators.required),
      dateRange: new FormControl(null, Validators.required)
    });
    formGroup.controls.dateRange.disable();
    formGroup.valueChanges
      .pipe(
        filter(() => this.formGroup.valid),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(formValue => {
        const selection = formValue.siteSelection;
        const siteIds: SiteId[] | null = Array.isArray(selection)
          ? (selection.length ? selection : null)
          : (selection != null ? [selection] : null);
        this.onChangeFn({
          siteIds,
          ...formValue.dateRange
        });
      });
    return formGroup;
  }
}
